import React, { useCallback, useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import freightService from "../../service/freight.service";
import polyline from "@mapbox/polyline";
import axios from "axios";
import "./mapa-comp.css";

/** Base clara (Carto), boa leitura de rotas e ícones — alinhada ao dashboard. */
const TILE_LIGHT =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

const FORTIO_NAVY = "#012443";
const FORTIO_ORANGE = "#FF3100";
/** Rota planejada: azul suave, menos agressivo que o anterior. */
const ROUTE_PLANNED_COLOR = "#8EB8D4";
const GPS_PANEL_LIMIT = 10;
const MAX_NEW_GEOCODES_PER_CYCLE = 3;
const GOOGLE_MAPS_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyAds1TwGzvflRvD8KHrbRHrnF3DvATFM1k";

/** Tooltip ao passar o mouse: mostra lat/lng (e opcionalmente título / texto extra em HTML). */
function tooltipCoordsOptions(extra = {}) {
  return {
    sticky: true,
    direction: "top",
    opacity: 0.95,
    className: "mapa-comp-coords-tooltip",
    ...extra,
  };
}

/** Converte timestamp Firestore, número (ms) ou string para texto pt-BR. */
function toLocaleDateTimeString(raw) {
  if (raw === undefined || raw === null || raw === "") {
    return null;
  }
  if (typeof raw.toDate === "function") {
    const d = raw.toDate();
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString("pt-BR");
  }
  const d = new Date(typeof raw === "number" ? raw : raw);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString("pt-BR");
}

function escapeHtml(text) {
  if (text === undefined || text === null) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Bloco padrão: título opcional, data/hora, lat, lng (tooltip e popup).
 */
function htmlLocationDetails({ title, lat, lng, dateTimeStr }) {
  const la = Number(lat);
  const lo = Number(lng);
  if (Number.isNaN(la) || Number.isNaN(lo)) {
    return "Coordenadas inválidas";
  }
  const lines = [];
  if (title) {
    lines.push(`<strong>${title}</strong>`);
  }
  lines.push(
    `<strong>Data e hora:</strong> ${dateTimeStr || "—"}`
  );
  lines.push(`<strong>Lat:</strong> ${la.toFixed(6)}`);
  lines.push(`<strong>Lng:</strong> ${lo.toFixed(6)}`);
  return lines.join("<br/>");
}

/**
 * Tooltip/popup do motorista: inclui endereço (geocodificação reversa) quando disponível.
 */
function htmlDriverMarkerContent({ title, lat, lng, dateTimeStr, addressText }) {
  let html = htmlLocationDetails({ title, lat, lng, dateTimeStr });
  if (addressText) {
    html += `<br/><br/><strong>Endereço:</strong><br/>${escapeHtml(addressText)}`;
  }
  return html;
}

/** Objeto estruturado de uma captura GPS (mapa + painel lateral). */
function buildGpsCaptureEntry({ lat, lng, timestamp, addressText }) {
  const dateTimeStr = toLocaleDateTimeString(timestamp) || "—";
  const key = `${lat}|${lng}|${timestamp || "sem-ts"}`;
  return {
    key,
    lat,
    lng,
    timestamp,
    dateTimeStr,
    addressText: addressText || null,
    captureHtml: htmlDriverMarkerContent({
      title: "Captura GPS",
      lat,
      lng,
      dateTimeStr,
      addressText,
    }),
  };
}

function sortByTimestampNewestFirst(items) {
  return [...items].sort((a, b) => {
    const ta = a.timestamp ? Number(a.timestamp) : 0;
    const tb = b.timestamp ? Number(b.timestamp) : 0;
    return tb - ta;
  });
}

function addressCacheKey(lat, lng) {
  return `${Number(lat).toFixed(4)}|${Number(lng).toFixed(4)}`;
}

/**
 * Endereço legível a partir de lat/lng (Google Geocoding API — mesma stack do restante do app).
 */
async function reverseGeocodeAddress(lat, lng, geocodingBlockedRef) {
  if (geocodingBlockedRef?.current) {
    return null;
  }

  const la = Number(lat);
  const lo = Number(lng);
  if (Number.isNaN(la) || Number.isNaN(lo)) {
    return null;
  }

  try {
    const { data } = await axios.get(
      "https://maps.googleapis.com/maps/api/geocode/json",
      {
        params: {
          latlng: `${la},${lo}`,
          key: GOOGLE_MAPS_KEY,
          language: "pt-BR",
        },
        timeout: 10000,
      }
    );

    if (data?.status === "OK" && data.results?.[0]?.formatted_address) {
      return data.results[0].formatted_address;
    }

    if (
      data?.status === "REQUEST_DENIED" ||
      data?.status === "OVER_QUERY_LIMIT" ||
      data?.status === "UNKNOWN_ERROR"
    ) {
      if (geocodingBlockedRef) {
        geocodingBlockedRef.current = true;
      }
      console.warn(
        "Geocodificação reversa desativada:",
        data.status,
        data.error_message || ""
      );
    }

    return null;
  } catch (e) {
    console.warn("Geocodificação reversa indisponível:", e?.message || e);
    return null;
  }
}

function normalizeCoord(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function extractLatLng(position) {
  const lat = normalizeCoord(position?.latitude ?? position?.lat);
  const lng = normalizeCoord(position?.longitude ?? position?.lng);
  return { lat, lng };
}

function pickNewestPositionId(positions) {
  if (!positions?.length) {
    return null;
  }
  const newest = positions.reduce((best, item) => {
    const ts = Number(item?.timestamp) || 0;
    const bestTs = Number(best?.timestamp) || 0;
    return ts >= bestTs ? item : best;
  }, null);
  return newest?.id || null;
}

function mapDocsToCaptures(snap) {
  return snap.docs
    .map((doc) => {
      const data = doc.data();
      const { lat, lng } = extractLatLng(data);
      return {
        id: doc.id,
        lat,
        lng,
        timestamp: data.timestamp,
      };
    })
    .filter((item) => item.lat !== null && item.lng !== null);
}

async function loadPanelCaptures(idFreight) {
  const snap = await freightService.searchRecentPositions(
    idFreight,
    GPS_PANEL_LIMIT
  );
  return mapDocsToCaptures(snap);
}

/**
 * Marcador do motorista: caminhão em SVG (estilo “shipping” / frota) + disco branco.
 * Não depende de arquivo em /public; contraste bom no mapa claro.
 */
function createDriverTruckDivIcon() {
  const html = `
    <div class="mapa-comp-truck-marker" title="Posição do motorista">
      <svg width="44" height="44" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="10.5" fill="#ffffff" stroke="${FORTIO_NAVY}" stroke-width="1.5"/>
        <path fill="${FORTIO_ORANGE}" d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
      </svg>
    </div>
  `;
  return L.divIcon({
    className: "mapa-comp-driver-divicon",
    html,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -20],
  });
}

const MapComponent = ({
  points = [],
  idFreight,
  searchLatestPositions,
  mapHeight = 500,
  /** Ex.: freight.status.describe — usado para esconder a linha reta do GPS durante "Em transito". */
  freightStatusDescribe,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const searchLatestPositionsRef = useRef(searchLatestPositions);
  const lastPositionReceivedRef = useRef(null);
  /** Cache persistente por coordenada (evita reconsultar a API a cada polling). */
  const addressCacheMapRef = useRef(new Map());
  /** Desliga geocodificação após erro de quota/permissão da API. */
  const geocodingBlockedRef = useRef(false);
  const [map, setMap] = useState(null);
  const [gpsCaptures, setGpsCaptures] = useState([]);

  searchLatestPositionsRef.current = searchLatestPositions;

  const getCachedOrFetchAddress = useCallback(async (lat, lng) => {
    const key = addressCacheKey(lat, lng);
    if (addressCacheMapRef.current.has(key)) {
      return addressCacheMapRef.current.get(key);
    }
    const text = await reverseGeocodeAddress(lat, lng, geocodingBlockedRef);
    addressCacheMapRef.current.set(key, text);
    return text;
  }, []);

  /** Geocodifica no máximo N coordenadas novas por ciclo; prioriza as mais recentes. */
  const buildGpsCaptureEntries = useCallback(
    async (positions) => {
      const sorted = sortByTimestampNewestFirst(positions);
      let newGeocodes = 0;
      const entries = [];

      for (const position of sorted) {
        const key = addressCacheKey(position.lat, position.lng);
        let addressText = null;

        if (addressCacheMapRef.current.has(key)) {
          addressText = addressCacheMapRef.current.get(key);
        } else if (
          newGeocodes < MAX_NEW_GEOCODES_PER_CYCLE &&
          !geocodingBlockedRef.current
        ) {
          addressText = await getCachedOrFetchAddress(position.lat, position.lng);
          newGeocodes += 1;
        }

        entries.push(
          buildGpsCaptureEntry({
            lat: position.lat,
            lng: position.lng,
            timestamp: position.timestamp,
            addressText,
          })
        );
      }

      return entries;
    },
    [getCachedOrFetchAddress]
  );

  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;

    const mapInstance = L.map(el, {
      zoomControl: true,
      attributionControl: true,
    }).setView([-23.986178, -46.308402], 13);
    mapRef.current = mapInstance;
    setMap(mapInstance);

    L.tileLayer(TILE_LIGHT, {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 20,
    }).addTo(mapInstance);

    points.forEach((point) => {
      let iconUrl;

      if (point.operation && point.operation.includes("carga")) {
        iconUrl = point.concluded
          ? `${process.env.PUBLIC_URL}/coleta-realizada.png`
          : `${process.env.PUBLIC_URL}/coleta-pendente.png`;
      } else if (point.operation && point.operation.includes("descarga")) {
        iconUrl = point.concluded
          ? `${process.env.PUBLIC_URL}/entrega-realizada.png`
          : `${process.env.PUBLIC_URL}/entrega-pendente.png`;
      } else {
        console.error("Ponto inválido ou sem operação definida:", point);
        return;
      }

      const icon = L.icon({
        iconUrl,
        iconSize: [50, 50],
        iconAnchor: [20, 42],
        popupAnchor: [-3, -32],
      });

      const [lat, lng] = point.coords || [];
      const stopDateTime = toLocaleDateTimeString(
        point.locationTime ?? point.timestamp ?? point.createdAt
      );
      const stopDetails = htmlLocationDetails({
        title: "Ponto de parada",
        lat,
        lng,
        dateTimeStr: stopDateTime,
      });
      const m = L.marker(point.coords, { icon }).addTo(mapInstance);
      m.bindPopup(
        `${point.popupText || "Sem descrição"}<br/><br/>${stopDetails}`
      );
      m.bindTooltip(stopDetails, tooltipCoordsOptions());
    });

    const fetchRoute = async () => {
      try {
        const coordinates = points.map((point) => point?.coords);
        console.log("Pontos enviados para a API de rotas:", coordinates);
        const stoppings_ = await freightService.searchFirstWay(idFreight);
        const startPosition = stoppings_.docs.map((stopping) => ({
          latitude: stopping?.data()?.latitude || 0,
          longitude: stopping?.data()?.longitude || 0,
        }));

        console.log("Posição inicial do motorista:", startPosition);
        if (
          startPosition.length === 0 ||
          !startPosition[0].latitude ||
          !startPosition[0].longitude
        ) {
          console.error("Erro: Posição inicial inválida do motorista");
          return;
        }

        const response = await axios.post(
          "https://8t7wcp4ip0.execute-api.us-east-1.amazonaws.com/dev/directions",
          {
            coordinates:
              startPosition[0].longitude && startPosition[0].latitude
                ? [
                    [startPosition[0].longitude, startPosition[0].latitude],
                    ...coordinates.map((coord) => [coord[1], coord[0]]),
                  ]
                : [...coordinates.map((coord) => [coord[1], coord[0]])],
          }
        );

        console.log("Resposta da API de rotas:", response.data);

        if (response.data.routes && response.data.routes.length > 0) {
          const route = polyline.decode(response.data.routes[0].geometry);

          console.log("Decodificação da rota:", route);

          const polylineLayer = L.polyline(route, {
            color: ROUTE_PLANNED_COLOR,
            weight: 5,
            opacity: 0.78,
            lineJoin: "round",
            lineCap: "round",
          }).addTo(mapInstance);
          mapInstance.fitBounds(polylineLayer.getBounds(), { padding: [28, 28] });

          const driverIcon = createDriverTruckDivIcon();

          const initialDriverPosition = [route[0][0], route[0][1]];
          const driverMarkerInstance = L.marker(initialDriverPosition, {
            icon: driverIcon,
            zIndexOffset: 1000,
          }).addTo(mapInstance);

          const initialAddr = await getCachedOrFetchAddress(
            initialDriverPosition[0],
            initialDriverPosition[1]
          );
          const initialDriverHtml = htmlDriverMarkerContent({
            title: "Motorista (tempo real)",
            lat: initialDriverPosition[0],
            lng: initialDriverPosition[1],
            dateTimeStr: null,
            addressText: initialAddr,
          });
          driverMarkerInstance.bindTooltip(initialDriverHtml, tooltipCoordsOptions());
          driverMarkerInstance.bindPopup(initialDriverHtml);

          driverMarkerRef.current = driverMarkerInstance;
        } else {
          console.error("Erro: Nenhuma rota foi retornada pela API.");
        }
      } catch (error) {
        console.error(
          "Erro ao buscar rota:",
          error.response ? error.response.data : error.message
        );
      }
    };

    fetchRoute();

    requestAnimationFrame(() => {
      mapInstance.invalidateSize();
    });

    return () => {
      mapInstance.remove();
      mapRef.current = null;
      driverMarkerRef.current = null;
      lastPositionReceivedRef.current = null;
      addressCacheMapRef.current.clear();
      geocodingBlockedRef.current = false;
      setGpsCaptures([]);
      setMap(null);
    };
  }, [points, idFreight, getCachedOrFetchAddress]);

  useEffect(() => {
    if (!map || typeof searchLatestPositionsRef.current !== "function") {
      return undefined;
    }

    const updateDriverPosition = async () => {
      const mapInstance = mapRef.current;
      if (!mapInstance) {
        return;
      }

      let panelCaptures = [];
      try {
        panelCaptures = await loadPanelCaptures(idFreight);
      } catch (error) {
        console.error("Erro ao buscar capturas GPS:", error);
      }

      const fetchLatestPositions = searchLatestPositionsRef.current;
      if (typeof fetchLatestPositions === "function") {
        try {
          const latestPositions = await fetchLatestPositions(
            lastPositionReceivedRef.current
          );
          if (latestPositions?.length) {
            const latestId = pickNewestPositionId(latestPositions);
            if (latestId) {
              lastPositionReceivedRef.current = latestId;
            }
          }
        } catch (error) {
          console.error("Erro ao sincronizar posições incrementais:", error);
        }
      }

      const stoppings = await freightService.searchCurrentWay(idFreight);
      const currentPosData = stoppings.docs[0]?.data() || {};
      const currentLat = normalizeCoord(currentPosData.latitude);
      const currentLng = normalizeCoord(currentPosData.longitude);
      const driverPosTime = toLocaleDateTimeString(currentPosData.timestamp);

      let driverMarker = driverMarkerRef.current;
      if (
        !driverMarker &&
        currentLat !== null &&
        currentLng !== null
      ) {
        driverMarker = L.marker([currentLat, currentLng], {
          icon: createDriverTruckDivIcon(),
          zIndexOffset: 1000,
        }).addTo(mapInstance);
        driverMarkerRef.current = driverMarker;
      }

      if (
        driverMarker &&
        currentLat !== null &&
        currentLng !== null
      ) {
        driverMarker.setLatLng([currentLat, currentLng]);
        const addr = await getCachedOrFetchAddress(currentLat, currentLng);
        const html = htmlDriverMarkerContent({
          title: "Motorista (tempo real)",
          lat: currentLat,
          lng: currentLng,
          dateTimeStr: driverPosTime,
          addressText: addr,
        });
        const tip = driverMarker.getTooltip();
        if (tip) {
          tip.setContent(html);
        } else {
          driverMarker.bindTooltip(html, tooltipCoordsOptions());
        }
        const popup = driverMarker.getPopup();
        if (popup) {
          popup.setContent(html);
        } else {
          driverMarker.bindPopup(html);
        }
      }

      const gpsCaptureEntries = await buildGpsCaptureEntries(panelCaptures);
      setGpsCaptures(sortByTimestampNewestFirst(gpsCaptureEntries));
    };

    updateDriverPosition();
    const interval = setInterval(updateDriverPosition, 10000);
    return () => clearInterval(interval);
  }, [map, idFreight, buildGpsCaptureEntries]);

  return (
    <div className="mapa-comp-layout" style={{ height: mapHeight }}>
      <div ref={mapContainerRef} className="mapa-comp-wrap" />
      <aside className="mapa-comp-gps-panel" aria-label="Capturas GPS">
        <header className="mapa-comp-gps-panel-header">
          <h3>Capturas GPS</h3>
          {gpsCaptures.length > 0 && (
            <span className="mapa-comp-gps-count">
              {gpsCaptures.length}/{GPS_PANEL_LIMIT}
            </span>
          )}
        </header>
        <ul className="mapa-comp-gps-list">
          {gpsCaptures.length === 0 ? (
            <li className="mapa-comp-gps-empty">Aguardando capturas GPS...</li>
          ) : (
            gpsCaptures.map((capture, index) => (
              <li
                key={capture.key}
                className={
                  index === 0
                    ? "mapa-comp-gps-item mapa-comp-gps-item--latest"
                    : "mapa-comp-gps-item"
                }
              >
                <span className="mapa-comp-gps-item-badge" aria-hidden="true" />
                <div className="mapa-comp-gps-item-body">
                  <strong>Captura GPS</strong>
                  <dl className="mapa-comp-gps-item-details">
                    <div>
                      <dt>Data e hora</dt>
                      <dd>{capture.dateTimeStr}</dd>
                    </div>
                    <div>
                      <dt>Endereço</dt>
                      <dd>{capture.addressText || "—"}</dd>
                    </div>
                  </dl>
                </div>
              </li>
            ))
          )}
        </ul>
      </aside>
    </div>
  );
};

export default MapComponent;
